import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
    Building2, ChevronRight, CheckCircle2, Utensils, UtensilsCrossed, Store,
    ShoppingCart, Pill, Wheat, Wine, ShoppingBag, ArrowLeft, Loader2,
    Star, Truck, Clock, Phone, MapPin
} from 'lucide-react';

const SUPER_CATEGORIES = [
    { id: 'okfood', name: 'OkFood', icon: '🍔', color: '#ea580c', desc: 'Comida rápida, antojitos, tacos, tortas, pizza…', business_types: ['fast_food', 'antojitos', 'tacos', 'pizza', 'tortas', 'sushi', 'otro'] },
    { id: 'okrestaurante', name: 'OkRestaurante', icon: '🍽️', color: '#1d4ed8', desc: 'Restaurantes de todas las cocinas', business_types: ['restaurante', 'mariscos', 'parrilla', 'italiana', 'japonesa', 'mexicana', 'otro'] },
    { id: 'oktienda', name: 'OkTienda', icon: '🏪', color: '#16a34a', desc: 'Abarrotes, minisuper, cremería, tiendita de barrio', business_types: ['abarrotes', 'minisuper', 'cremeria', 'convenience', 'otro'] },
    { id: 'okshop', name: 'OkShop', icon: '🏬', color: '#7c3aed', desc: 'Retail, carnicería, pollería, ferretería, florería…', business_types: ['carniceria', 'polleria', 'ferreteria', 'floreria', 'tlapaleria', 'retail', 'otro'] },
    { id: 'okfarmacia', name: 'OkFarma', icon: '💊', color: '#0891b2', desc: 'Farmacias, homeopáticas, droguerías', business_types: ['farmacia', 'homeopatica', 'drogueria', 'otro'] },
    { id: 'oktortilla', name: 'OkTortilla', icon: '🌽', color: '#d97706', desc: 'Tortillerías', business_types: ['tortilleria'] },
    { id: 'okdrink', name: 'OkDrink', icon: '🍺', color: '#b45309', desc: 'Vinaterías, cervecerías, licorerías', business_types: ['vinateria', 'cerveceria', 'licoreria', 'otro'] },
];

function StepIndicator({ steps, current }) {
    return (
        <div className="flex items-center gap-1">
            {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-1">
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < current ? 'bg-green-500 text-white' : i === current ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {i < current ? '✓' : i + 1}
                    </div>
                    {i < steps.length - 1 && <div className={`h-0.5 w-6 ${i < current ? 'bg-green-400' : 'bg-gray-200'}`} />}
                </div>
            ))}
        </div>
    );
}

export default function OkNegocioRegister() {
    const { role, user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0); // 0: pick category, 1: form, 2: success
    const [selectedCat, setSelectedCat] = useState(null);
    const [form, setForm] = useState({
        name: '',
        business_type: '',
        tagline: '',
        description: '',
        city: '',
        address: '',
        phone: '',
        whatsapp: '',
        email: '',
        delivery_available: false,
        delivery_cost: 0,
        min_order: 0,
        payment_methods: ['cash'],
    });
    const [submitting, setSubmitting] = useState(false);
    const [myBiz, setMyBiz] = useState(null);
    const [loadingBiz, setLoadingBiz] = useState(true);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (role === 'user') {
            api.get('/me/oknegocio').then(r => setMyBiz(r.data)).catch(() => setMyBiz(null)).finally(() => setLoadingBiz(false));
        } else {
            setLoadingBiz(false);
        }
    }, [role]);

    const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const togglePayment = (method) => {
        setForm(prev => ({
            ...prev,
            payment_methods: prev.payment_methods.includes(method)
                ? prev.payment_methods.filter(m => m !== method)
                : [...prev.payment_methods, method],
        }));
    };

    const handleSubmit = async () => {
        if (!form.name.trim() || !form.city.trim() || !form.phone.trim()) {
            toast.error('Completa los campos obligatorios (nombre, ciudad, teléfono)');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post('/oknegocios/register', {
                ...form,
                super_category: selectedCat.id,
                delivery_cost: Number(form.delivery_cost),
                min_order: Number(form.min_order),
            });
            setResult(data);
            setStep(2);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al registrar el negocio');
        } finally {
            setSubmitting(false);
        }
    };

    // Not logged in
    if (role !== 'user' && !loadingBiz) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center bg-white rounded-3xl p-8 shadow-sm border">
                    <div className="text-5xl mb-4">🏪</div>
                    <h1 className="text-2xl font-black mb-2">Crea tu OkNegocio</h1>
                    <p className="text-muted-foreground mb-6">Necesitas una cuenta OkUser para registrar tu negocio. Es gratis y tarda menos de 2 minutos.</p>
                    <div className="space-y-3">
                        <Link to="/acceso" state={{ intent: 'oknegocio' }}>
                            <Button className="w-full bg-red-600 hover:bg-red-700 rounded-full gap-2">
                                <Building2 className="h-4 w-4" />Crear cuenta e iniciar
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="outline" className="w-full rounded-full">Ya tengo cuenta — Ingresar</Button>
                        </Link>
                        <Link to="/okgo">
                            <Button variant="ghost" className="w-full rounded-full">Explorar OkGo</Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Already has a business
    if (myBiz && step === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center bg-white rounded-3xl p-8 shadow-sm border">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h1 className="text-xl font-black mb-1">Ya tienes un OkNegocio</h1>
                    <p className="text-muted-foreground mb-1"><strong>{myBiz.name}</strong></p>
                    <Badge className={myBiz.status === 'active' ? 'bg-green-100 text-green-700 border-0' : 'bg-amber-100 text-amber-700 border-0 mb-4'}>
                        {myBiz.status === 'active' ? '✓ Activo' : 'En revisión'}
                    </Badge>
                    <p className="text-sm text-muted-foreground mb-6">
                        {myBiz.status !== 'active' ? 'Tu negocio está siendo revisado por el equipo de OkGo. Te notificaremos pronto.' : ''}
                    </p>
                    <div className="space-y-2">
                        <Link to={`/aliado/${myBiz.slug}`}>
                            <Button className="w-full bg-red-600 hover:bg-red-700 rounded-full">Ver mi perfil público</Button>
                        </Link>
                        <Link to="/dashboard">
                            <Button variant="outline" className="w-full rounded-full">Ir a mi dashboard</Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Success
    if (step === 2 && result) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center bg-white rounded-3xl p-8 shadow-sm border">
                    <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                        <CheckCircle2 className="h-10 w-10 text-green-500" />
                    </div>
                    <h1 className="text-2xl font-black mb-2">¡Negocio registrado!</h1>
                    <p className="text-muted-foreground mb-1"><strong>{result.name}</strong> está en revisión.</p>
                    <p className="text-sm text-muted-foreground mb-6">El equipo de OkGo revisará tu información y te notificará en 24-48 horas.</p>
                    <div className="space-y-2">
                        <Link to={`/aliado/${result.slug}`}>
                            <Button className="w-full bg-red-600 hover:bg-red-700 rounded-full">Ver mi perfil</Button>
                        </Link>
                        <Link to="/dashboard">
                            <Button variant="outline" className="w-full rounded-full">Ir a mi dashboard</Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
                <button
                    onClick={() => step > 0 ? setStep(step - 1) : navigate('/okgo')}
                    className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex-1">
                    <h1 className="font-bold text-sm">Registrar OkNegocio</h1>
                    <p className="text-xs text-muted-foreground">Paso {step + 1} de 2</p>
                </div>
                <StepIndicator steps={['Categoría', 'Datos']} current={step} />
            </div>

            <div className="max-w-2xl mx-auto px-4 py-6">
                {/* Step 0: pick super category */}
                {step === 0 && (
                    <div>
                        <div className="text-center mb-8">
                            <div className="text-4xl mb-3">🏪</div>
                            <h2 className="text-2xl font-black mb-1">¿Qué tipo de negocio tienes?</h2>
                            <p className="text-muted-foreground text-sm">Selecciona la categoría que mejor describe tu negocio</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {SUPER_CATEGORIES.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => { setSelectedCat(cat); setField('business_type', cat.business_types[0]); setStep(1); }}
                                    className="flex items-start gap-4 p-4 rounded-2xl border bg-white text-left hover:border-red-300 hover:shadow-md transition-all group"
                                >
                                    <div
                                        className="h-12 w-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-110 transition-transform"
                                        style={{ backgroundColor: cat.color + '20' }}
                                    >
                                        {cat.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm" style={{ color: cat.color }}>{cat.name}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{cat.desc}</p>
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1 group-hover:text-red-500 transition-colors" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 1: form */}
                {step === 1 && selectedCat && (
                    <div>
                        <div className="flex items-center gap-3 mb-6 p-3 rounded-2xl border bg-white">
                            <span className="text-3xl">{selectedCat.icon}</span>
                            <div>
                                <p className="font-bold text-sm" style={{ color: selectedCat.color }}>{selectedCat.name}</p>
                                <p className="text-xs text-muted-foreground">{selectedCat.desc}</p>
                            </div>
                            <button onClick={() => setStep(0)} className="ml-auto text-xs text-muted-foreground hover:text-red-600 border rounded-full px-2 py-1">Cambiar</button>
                        </div>

                        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
                            <h3 className="font-bold text-base">Información del negocio</h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Nombre del negocio *</Label>
                                    <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Ej: Tacos El Güero" className="mt-1" />
                                </div>
                                <div>
                                    <Label>Tipo de negocio</Label>
                                    <Select value={form.business_type} onValueChange={v => setField('business_type', v)}>
                                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {selectedCat.business_types.map(bt => (
                                                <SelectItem key={bt} value={bt}>{bt.charAt(0).toUpperCase() + bt.slice(1).replace(/_/g, ' ')}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <Label>Slogan / Tagline</Label>
                                <Input value={form.tagline} onChange={e => setField('tagline', e.target.value)} placeholder="Ej: Los tacos más ricos de la colonia" className="mt-1" />
                            </div>

                            <div>
                                <Label>Descripción</Label>
                                <Textarea value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Cuéntanos sobre tu negocio…" className="mt-1" rows={3} />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Ciudad *</Label>
                                    <Input value={form.city} onChange={e => setField('city', e.target.value)} placeholder="Ej: Ciudad de México" className="mt-1" />
                                </div>
                                <div>
                                    <Label>Dirección</Label>
                                    <Input value={form.address} onChange={e => setField('address', e.target.value)} placeholder="Calle y número" className="mt-1" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Teléfono *</Label>
                                    <Input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="55 1234 5678" className="mt-1" />
                                </div>
                                <div>
                                    <Label>WhatsApp</Label>
                                    <Input value={form.whatsapp} onChange={e => setField('whatsapp', e.target.value)} placeholder="5512345678" className="mt-1" />
                                </div>
                            </div>

                            <div>
                                <Label>Correo electrónico</Label>
                                <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="negocio@ejemplo.mx" className="mt-1" />
                            </div>

                            {/* Delivery */}
                            <div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="delivery"
                                        checked={form.delivery_available}
                                        onChange={e => setField('delivery_available', e.target.checked)}
                                        className="h-4 w-4 accent-red-600"
                                    />
                                    <Label htmlFor="delivery" className="cursor-pointer">¿Ofreces servicio de delivery?</Label>
                                </div>
                                {form.delivery_available && (
                                    <div className="mt-3 grid grid-cols-2 gap-3 pl-7">
                                        <div>
                                            <Label className="text-xs">Costo de envío ($)</Label>
                                            <Input type="number" min="0" value={form.delivery_cost} onChange={e => setField('delivery_cost', e.target.value)} className="mt-1" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Pedido mínimo ($)</Label>
                                            <Input type="number" min="0" value={form.min_order} onChange={e => setField('min_order', e.target.value)} className="mt-1" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment methods */}
                            <div>
                                <Label className="block mb-2">Métodos de pago</Label>
                                <div className="flex gap-2 flex-wrap">
                                    {[{ id: 'cash', label: '💵 Efectivo' }, { id: 'card', label: '💳 Tarjeta' }, { id: 'transfer', label: '🏦 Transferencia' }].map(pm => (
                                        <button
                                            key={pm.id}
                                            type="button"
                                            onClick={() => togglePayment(pm.id)}
                                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.payment_methods.includes(pm.id) ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'}`}
                                        >
                                            {pm.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <Button
                                className="w-full bg-red-600 hover:bg-red-700 rounded-full gap-2 mt-2"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />Registrando…</> : <><CheckCircle2 className="h-4 w-4" />Registrar mi OkNegocio</>}
                            </Button>

                            <p className="text-xs text-muted-foreground text-center">
                                El equipo de OkGo revisará tu información en 24-48 hrs. Se te notificará por correo.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
